import { useState, useMemo, useEffect } from 'react';
import { useSearchParams, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import IssueCard from '@/pages/issues/components/IssueCard';
import PostModal from '@/pages/issues/components/PostModal';
import UploadModal from '@/pages/issues/components/UploadModal';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAdmin } from '@/contexts/AdminContext';
import { usePosts } from '@/contexts/PostsContext';
import { Post, Category } from '@/types/post';

const CATEGORIES: Category[] = [
  'Global Affairs',
  'International Order',
  'Rights and Freedoms',
  'Politics',
  'Justice and Freedom',
  'Law and Society',
];

const CATEGORY_ICONS: Record<string, string> = {
  'Global Affairs': 'ri-earth-line',
  'International Order': 'ri-building-line',
  'Rights and Freedoms': 'ri-heart-line',
  'Politics': 'ri-government-line',
  'Justice and Freedom': 'ri-scales-line',
  'Law and Society': 'ri-book-2-line',
};

const CATEGORY_DESCRIPTIONS: Record<string, { en: string; ko: string }> = {
  'Global Affairs': {
    en: 'International events, geopolitics, and the forces shaping our world.',
    ko: '국제 사건, 지정학, 그리고 세계를 형성하는 힘들.',
  },
  'International Order': {
    en: 'The rules, institutions, and norms that govern relations between states.',
    ko: '국가 간 관계를 규율하는 규칙, 기관, 규범.',
  },
  'Rights and Freedoms': {
    en: 'Human rights, civil liberties, and the ongoing struggle for dignity.',
    ko: '인권, 시민의 자유, 그리고 존엄성을 위한 끊임없는 투쟁.',
  },
  'Politics': {
    en: 'Power, governance, democracy, and political systems around the world.',
    ko: '권력, 거버넌스, 민주주의, 그리고 세계의 정치 체계.',
  },
  'Justice and Freedom': {
    en: 'The pursuit of justice, accountability, and freedom in society.',
    ko: '사회에서 정의, 책임, 자유의 추구.',
  },
  'Law and Society': {
    en: 'How law shapes society and how society shapes law.',
    ko: '법이 사회를 형성하는 방식, 그리고 사회가 법을 형성하는 방식.',
  },
};

const IssuesPage = () => {
  const { t, lang } = useLanguage();
  const { isAdmin } = useAdmin();
  const { posts, isLoading } = usePosts();
  const [searchParams, setSearchParams] = useSearchParams();
  const { slug } = useParams<{ slug?: string }>();
  const [selectedPost, setSelectedPost] = useState<Post | null>(null);
  const [showUpload, setShowUpload] = useState(false);
  const [editPost, setEditPost] = useState<Post | null>(null);
  const [sortBy, setSortBy] = useState<'latest' | 'popular'>('latest');

  const initialCategory = useMemo<Category | 'All'>(() => {
    const param = searchParams.get('category');
    if (param && CATEGORIES.includes(param as Category)) {
      return param as Category;
    }
    return 'All';
  }, [searchParams]);

  const [activeCategory, setActiveCategory] = useState<Category | 'All'>(initialCategory);

  useEffect(() => {
    const param = searchParams.get('category');
    if (param && CATEGORIES.includes(param as Category)) {
      setActiveCategory(param as Category);
    } else {
      setActiveCategory('All');
    }
  }, [searchParams]);

  // Open modal from URL slug or post query param
  useEffect(() => {
    if (slug) {
      const found = posts.find((p) => p.slug === slug);
      if (found) setSelectedPost(found);
    } else {
      const postId = searchParams.get('post');
      if (postId) {
        const found = posts.find((p) => String(p.id) === postId);
        if (found) setSelectedPost(found);
      }
    }
  }, [slug, searchParams, posts]);

  const handleCategoryChange = (cat: Category | 'All') => {
    setActiveCategory(cat);
    if (cat === 'All') {
      setSearchParams({});
    } else {
      setSearchParams({ category: cat });
    }
  };

  const filtered = useMemo(() => {
    const visible = isAdmin ? posts : posts.filter((p) => p.isPublic);
    let result = activeCategory === 'All' ? visible : visible.filter((p) => p.category === activeCategory);

    // Language filter: show posts for current language + 'both'
    result = result.filter((p) => p.language === 'both' || p.language === lang);

    if (sortBy === 'latest') {
      result = result.sort((a, b) => b.date.localeCompare(a.date));
    } else if (sortBy === 'popular') {
      result = result.sort((a, b) => {
        const aReactions = Object.values(a.emojiReactions).reduce((sum, v) => sum + v, 0);
        const bReactions = Object.values(b.emojiReactions).reduce((sum, v) => sum + v, 0);
        const aScore = aReactions * 3 + a.viewCount;
        const bScore = bReactions * 3 + b.viewCount;
        return bScore - aScore;
      });
    }

    return result;
  }, [posts, activeCategory, isAdmin, sortBy, lang]);

  const handleCloseModal = () => {
    setSelectedPost(null);
    if (slug) {
      window.history.replaceState({}, '', '/issues');
    }
  };

  return (
    <div className="min-h-screen bg-[#F8F7F4]">
      <Navbar />

      {/* Header */}
      <section className="relative pt-28 pb-16 px-6 bg-stone-900 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=abstract%20world%20map%20made%20of%20scattered%20dots%20and%20lines%2C%20deep%20navy%20charcoal%20background%2C%20faint%20gold%20grid%20lines%2C%20minimal%20editorial%20design%2C%20global%20network%20visual%2C%20cinematic%20dark%20atmosphere%2C%20high%20resolution%20texture&width=1600&height=400&seq=issues-hero-bg-v2&orientation=landscape"
            alt=""
            className="w-full h-full object-cover object-top opacity-15"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-stone-900/95 to-stone-900/70" />
        </div>
        <div className="relative z-10 max-w-5xl mx-auto">
          <p className="text-xs font-semibold text-stone-400 uppercase tracking-[0.2em] mb-3">
            {t('Visual Archive', '비주얼 아카이브')}
          </p>
          <h1 className="text-4xl md:text-5xl font-black text-white leading-none mb-3 tracking-tight">
            {t('Posts', '게시물')}
          </h1>
          <p className="text-stone-400 text-sm max-w-md leading-relaxed">
            {t(
              'A visual archive of global affairs, politics, human rights, and law — designed for clarity.',
              '국제 사건, 정치, 인권, 법률 관련 이미지 중심의 비주얼 아카이브.'
            )}
          </p>
        </div>
      </section>

      {/* Category Description */}
      {activeCategory !== 'All' && CATEGORY_DESCRIPTIONS[activeCategory] && (
        <section className="bg-stone-50 border-b border-stone-100 px-6 py-4">
          <div className="max-w-5xl mx-auto flex items-center gap-3">
            <div className="w-8 h-8 flex items-center justify-center bg-stone-800 rounded-lg shrink-0">
              <i className={`${CATEGORY_ICONS[activeCategory]} text-white text-sm`} />
            </div>
            <div>
              <p className="text-xs font-bold text-stone-700">{activeCategory}</p>
              <p className="text-xs text-stone-500 mt-0.5">
                {lang === 'ko' ? CATEGORY_DESCRIPTIONS[activeCategory].ko : CATEGORY_DESCRIPTIONS[activeCategory].en}
              </p>
            </div>
          </div>
        </section>
      )}

      {/* Category Filter */}
      <section className="sticky top-16 z-30 bg-white/90 backdrop-blur-md border-b border-stone-100">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center gap-2 overflow-x-auto">
          <button
            onClick={() => handleCategoryChange('All')}
            className={`flex items-center gap-1.5 text-xs px-4 py-2 rounded-full border font-semibold cursor-pointer transition-all whitespace-nowrap shrink-0 ${
              activeCategory === 'All'
                ? 'bg-stone-900 text-white border-stone-900'
                : 'border-stone-200 text-stone-500 hover:border-stone-400'
            }`}
          >
            <i className="ri-layout-grid-line text-xs" />
            {t('All', '전체')}
            <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${activeCategory === 'All' ? 'bg-white/20' : 'bg-stone-100 text-stone-500'}`}>
              {isAdmin ? posts.length : posts.filter((p) => p.isPublic).length}
            </span>
          </button>

          {CATEGORIES.map((cat) => (
            <button
              key={cat}
              onClick={() => handleCategoryChange(cat)}
              className={`flex items-center gap-1.5 text-xs px-4 py-2 rounded-full border font-semibold cursor-pointer transition-all whitespace-nowrap shrink-0 ${
                activeCategory === cat
                  ? 'bg-stone-900 text-white border-stone-900'
                  : 'border-stone-200 text-stone-500 hover:border-stone-300'
              }`}
            >
              <i className={`${CATEGORY_ICONS[cat]} text-xs`} />
              {cat}
            </button>
          ))}
        </div>
      </section>

      {/* Image Grid */}
      <section className="py-8 px-4 md:px-6">
        <div className="max-w-5xl mx-auto">
          {/* Admin: New Post Button */}
          {isAdmin && (
            <button
              onClick={() => setShowUpload(true)}
              className="mb-4 flex items-center gap-2 text-xs px-5 py-2.5 bg-stone-900 text-white rounded-lg hover:bg-stone-700 cursor-pointer transition-all whitespace-nowrap font-semibold"
            >
              <i className="ri-add-line text-sm" />
              {t('New Post', '새 게시물')}
            </button>
          )}

          {isLoading ? (
            <div className="text-center py-28">
              <i className="ri-loader-4-line text-stone-400 text-2xl animate-spin" />
              <p className="text-stone-400 text-sm mt-3">{t('Loading...', '불러오는 중...')}</p>
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-28">
              <div className="w-16 h-16 flex items-center justify-center bg-stone-100 rounded-2xl mx-auto mb-4">
                <i className="ri-image-line text-stone-400 text-2xl" />
              </div>
              <p className="text-stone-500 text-sm">{t('No posts found.', '게시물이 없습니다.')}</p>
            </div>
          ) : (
            <>
              <p className="text-xs text-stone-400 mb-4 font-medium flex items-center justify-between gap-3">
                <span>
                  {filtered.length}{t(' posts', '개의 게시물')}
                  {activeCategory !== 'All' && (
                    <span className="ml-2 text-stone-600 font-semibold">— {activeCategory}</span>
                  )}
                </span>
                {/* Sort toggle */}
                <span className="flex items-center gap-1 bg-white rounded-full border border-stone-200 p-0.5">
                  <button
                    onClick={() => setSortBy('latest')}
                    className={`text-[10px] font-semibold px-3 py-1 rounded-full cursor-pointer whitespace-nowrap transition-all ${
                      sortBy === 'latest'
                        ? 'bg-stone-900 text-white'
                        : 'text-stone-500 hover:text-stone-800'
                    }`}
                    title={lang === 'ko' ? '최신순' : 'Latest'}
                  >
                    <i className="ri-time-line mr-1" />
                    {t('Latest', '최신순')}
                  </button>
                  <button
                    onClick={() => setSortBy('popular')}
                    className={`text-[10px] font-semibold px-3 py-1 rounded-full cursor-pointer whitespace-nowrap transition-all ${
                      sortBy === 'popular'
                        ? 'bg-stone-900 text-white'
                        : 'text-stone-500 hover:text-stone-800'
                    }`}
                    title={lang === 'ko' ? '인기순 (리액션 + 조회수)' : 'Popular'}
                  >
                    <i className="ri-fire-line mr-1" />
                    {t('Popular', '인기순')}
                  </button>
                </span>
              </p>

              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-1 md:gap-2">
                {filtered.map((post) => (
                  <IssueCard
                    key={post.id}
                    post={post}
                    onClick={() => setSelectedPost(post)}
                    onEdit={() => { setEditPost(post); setShowUpload(true); }}
                  />
                ))}
              </div>
            </>
          )}
        </div>
      </section>

      <Footer />

      {/* Modals */}
      {selectedPost && (
        <PostModal post={selectedPost} onClose={handleCloseModal} onEdit={() => { setEditPost(selectedPost); setSelectedPost(null); setShowUpload(true); }} />
      )}
      {showUpload && (
        <UploadModal
          onClose={() => { setShowUpload(false); setEditPost(null); }}
          editPost={editPost}
        />
      )}
    </div>
  );
};

export default IssuesPage;